/* GrowthOS — Price Sheet record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors Product /
   Sales / Quote detail pages): back row, header card (tag glyph + name + Active badge
   + pricing/currency meta, owner pill, Edit + dots), chips, a horizontal tab bar, a
   read-only Overview (info cards) and the real Products (CommonProductList) / Accounts
   (ChildAccount) / Activity(Timeline) / Notes child tabs. Replaces the legacy left-nav
   scroll-spy that reused PriceForm in "view mode" (PriceForm is now a plain editable
   form). Same fetches as the legacy page (fetchDetail/Notes/AccountsAndAppendToPriceSheet/
   ProductsAndAppendToModule, module 'PriceSheet'). */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAccountsAndAppendToPriceSheet,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchProductsAndAppendToModule,
} from '../../redux/actions';
import { getPriceSheetById } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Timeline from '../sharedChild/timeline';
import ChildAccount from '../sharedChild/childAccount';
import CommonProductList from '../sharedChild/commonList/product/commonProductList';
import { Ic } from "../../shared/component/GosIcon";


const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const activeBadge = (on) => (
  <span className={'gos-badge gos-b-' + (on ? 'green' : 'gray')}>
    <span className="gos-pip" />
    {on ? 'Active' : 'Inactive'}
  </span>
);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

export const ViewPricelist = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'priceSheetList');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const priceSheetID = searchParams.get('id');

  const [priceSheetLocalData, setPriceSheetLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const priceSheetStoreData = useSelector((state) => getPriceSheetById(state, location.state?.id || priceSheetID));

  useEffect(() => {
    setPriceSheetLocalData((prev) => ({ ...prev, ...priceSheetStoreData }));
  }, [priceSheetStoreData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const priceSheetId = location.state?.id || priceSheetID;
    if (priceSheetId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId }));
      dispatch(fetchAccountsAndAppendToPriceSheet({ moduleName: 'PriceSheet', id: priceSheetId }));
      dispatch(fetchProductsAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, priceSheetLocalData?.id]);

  const cd = priceSheetLocalData || {};
  const owner = cd.priceSheetOwner;
  const active = !!cd.productActive;

  const handleEditPriceSheet = () => {
    setMenuOpen(false);
    navigate('/products/priceSheet/editPriceSheet', { state: { ...cd, type: 'view', from: 'view' } });
  };

  const chips = [
    cd.priceModel ? { icon: 'tag', label: cd.priceModel } : null,
    { icon: 'box', label: active ? 'Active' : 'Inactive', tone: active ? 'green' : 'gray' },
    owner && { icon: 'user', label: 'Owner ' + owner },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'products', label: 'Products', icon: 'box' },
    { id: 'accounts', label: 'Accounts', icon: 'building' },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
  ];

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Price Sheet Information"
          icon="tag"
          rows={[
            ['Price Sheet Owner', val(owner)],
            ['Pricing Model', val(cd.priceModel)],
            ['Currency', val(cd.currencyPrice)],
            ['Active', activeBadge(active)],
          ]}
        />
        <InfoCard
          title="Validity"
          icon="calendar"
          rows={[
            ['Start Date', val(fmtDate(cd.startDate))],
            ['End Date', val(fmtDate(cd.endDate))],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
        <InfoCard
          title="Discount"
          icon="percent"
          rows={[
            ['Discount Percent', val(cd.discountPercent) != null ? cd.discountPercent + '%' : null],
            ['Discount Amount', cd.discountAmount ? money(cd.discountAmount) : null],
          ]}
        />
      </div>
      <div className="gos-lr-grid2" style={{ marginTop: 16 }}>
        <TextCard title="Terms and Conditions" icon="template" text={cd.terms} />
        <TextCard title="Description" icon="note" text={cd.description} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'products') tabBody = <CommonProductList localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'accounts') tabBody = <ChildAccount localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'PriceSheet'} />;

  return (
    <>
      {loading && <Loader />}
      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/products/priceSheet')}>
            <Ic name="chevL" size={15} /> Price Sheets
          </button>
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
                <Ic name="tag" size={22} />
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {cd.name || 'Price Sheet'}
                  {activeBadge(active)}
                </div>
                <div className="gos-lr-subline">
                  <Ic name="tag" size={14} />
                  {cd.priceModel || '—'}
                  {cd.currencyPrice ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      {cd.currencyPrice}
                    </>
                  ) : null}
                  {fmtDate(cd.startDate) ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="calendar" size={14} />
                      {fmtDate(cd.startDate)}
                      {fmtDate(cd.endDate) ? ' → ' + fmtDate(cd.endDate) : ''}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {owner ? (
                <span className="gos-qr-owner">
                  <span className="gos-lr-ava" style={{ width: 24, height: 24, fontSize: 10.5, background: gosAvaColor(owner) }}>
                    {gosInitials(owner)}
                  </span>
                  {owner}
                </span>
              ) : null}
              <button type="button" className="gos-lr-contact-btn" onClick={handleEditPriceSheet} title="Edit price sheet">
                <Ic name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={handleEditPriceSheet}>
                      <Ic name="edit" size={15} /> Edit Price Sheet
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {tabBody}
      </div>
    </>
  );
};

export default ViewPricelist;
